import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { 
  Upload, Trash2, Edit2, LogOut, Image, Plus, X, Save, 
  Home, Sun, Moon, Check, AlertCircle, FolderPlus, Folder
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboardPage = () => {
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [images, setImages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('images');

  // Upload form state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadCategory, setUploadCategory] = useState('general');

  // Category form state
  const [newCategoryName, setNewCategoryName] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);

  const getAuthHeader = useCallback(() => {
    const token = localStorage.getItem('jpr_admin_token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const verifyAuth = useCallback(async () => {
    try {
      await axios.get(`${BACKEND_URL}/api/auth/verify`, {
        headers: getAuthHeader()
      });
    } catch (error) {
      localStorage.removeItem('jpr_admin_token');
      navigate('/admin');
    }
  }, [navigate, getAuthHeader]);

  const fetchImages = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/gallery`);
      setImages(response.data);
    } catch (error) {
      console.error('Error fetching images:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  useEffect(() => {
    verifyAuth();
    fetchImages();
    fetchCategories();
  }, [verifyAuth, fetchImages, fetchCategories]);

  const handleLogout = () => {
    localStorage.removeItem('jpr_admin_token');
    navigate('/admin');
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setUploadPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('title', uploadTitle);
    formData.append('description', uploadDescription);
    formData.append('category', uploadCategory);

    try {
      await axios.post(`${BACKEND_URL}/api/admin/gallery/upload`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });

      setMessage({ type: 'success', text: 'Image uploaded successfully!' });
      resetUploadForm();
      fetchImages();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Upload failed' });
    } finally {
      setUploading(false);
    }
  };

  const resetUploadForm = () => {
    setUploadFile(null);
    setUploadPreview(null);
    setUploadTitle('');
    setUploadDescription('');
    setUploadCategory('general');
    setShowUploadModal(false);
  };

  const handleUpdateImage = async () => {
    if (!editingImage) return;

    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/gallery/${editingImage.id}`,
        {
          title: editingImage.title,
          description: editingImage.description,
          category: editingImage.category
        },
        { headers: getAuthHeader() }
      );

      setMessage({ type: 'success', text: 'Image updated successfully!' });
      setEditingImage(null);
      fetchImages();
    } catch (error) {
      setMessage({ type: 'error', text: 'Update failed' });
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/admin/gallery/${imageId}`, {
        headers: getAuthHeader()
      });

      setMessage({ type: 'success', text: 'Image deleted successfully!' });
      fetchImages();
    } catch (error) {
      setMessage({ type: 'error', text: 'Delete failed' });
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!newCategoryName.trim()) return;

    setCreatingCategory(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/categories`,
        { name: newCategoryName.trim() },
        { headers: getAuthHeader() }
      );

      setMessage({ type: 'success', text: 'Category created successfully!' });
      setNewCategoryName('');
      setShowCategoryModal(false);
      fetchCategories();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to create category' });
    } finally {
      setCreatingCategory(false);
    }
  };

  const handleDeleteCategory = async (categoryId, categoryName) => {
    if (!window.confirm(`Are you sure you want to delete the category "${categoryName}"?`)) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/admin/categories/${categoryId}`, {
        headers: getAuthHeader()
      });

      setMessage({ type: 'success', text: 'Category deleted successfully!' });
      fetchCategories();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to delete category' });
    }
  };

  const getImageUrl = (url) => {
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  const getCategoryName = (slug) => {
    const cat = categories.find(c => c.slug === slug);
    return cat ? cat.name : slug;
  };

  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  return (
    <div className={`min-h-screen ${isDark ? 'bg-neutral-950' : 'bg-neutral-100'}`}>
      {/* Header */}
      <header className={`sticky top-0 z-40 border-b ${isDark ? 'bg-neutral-900 border-neutral-800' : 'bg-white border-neutral-200'}`}>
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-neutral-900'}`}>
              <span className="text-lime-500">JPR</span> Admin Dashboard
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-neutral-800 text-neutral-400' : 'hover:bg-neutral-100 text-neutral-600'}`}
            >
              <Home className="w-5 h-5" />
            </Link>
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-neutral-800 text-neutral-400' : 'hover:bg-neutral-100 text-neutral-600'}`}
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <button
              onClick={handleLogout}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${isDark ? 'hover:bg-neutral-800 text-neutral-400' : 'hover:bg-neutral-100 text-neutral-600'}`}
            >
              <LogOut className="w-5 h-5" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      {/* Message Toast */}
      {message.text && (
        <div className={`fixed top-20 right-4 z-50 px-6 py-3 rounded-lg flex items-center gap-2 ${
          message.type === 'success' ? 'bg-lime-500 text-black' : 'bg-red-500 text-white'
        }`}>
          {message.type === 'success' ? <Check className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          {message.text}
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8">
          <button
            onClick={() => setActiveTab('images')}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
              activeTab === 'images'
                ? 'bg-lime-500 text-black'
                : isDark
                  ? 'bg-neutral-800 text-neutral-300 hover:bg-neutral-700'
                  : 'bg-white text-neutral-700 hover:bg-neutral-50'
            }`}
          >
            <Image className="w-5 h-5" />
            Images
          </button>
          <button
            onClick={() => setActiveTab('categories')}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
              activeTab === 'categories'
                ? 'bg-lime-500 text-black'
                : isDark
                  ? 'bg-neutral-800 text-neutral-300 hover:bg-neutral-700'
                  : 'bg-white text-neutral-700 hover:bg-neutral-50'
            }`}
          >
            <Folder className="w-5 h-5" />
            Categories
          </button>
        </div>

        {/* Images Tab */}
        {activeTab === 'images' && (
          <>
            {/* Stats & Actions */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
              <div>
                <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-neutral-900'}`}>Gallery Manager</h2>
                <p className={isDark ? 'text-neutral-400' : 'text-neutral-600'}>
                  {images.length} image{images.length !== 1 ? 's' : ''} in gallery
                </p>
              </div>
              <button
                onClick={() => setShowUploadModal(true)}
                className="flex items-center gap-2 px-6 py-3 bg-lime-500 text-black font-semibold rounded-lg hover:bg-lime-400 transition-all"
              >
                <Plus className="w-5 h-5" />
                Upload Image
              </button>
            </div>

            {/* Gallery Grid */}
            {loading ? (
              <div className="flex justify-center py-20">
                <div className="w-12 h-12 border-4 border-lime-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : images.length === 0 ? (
              <div className={`text-center py-20 rounded-xl ${isDark ? 'bg-neutral-900' : 'bg-white'}`}>
                <Image className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-neutral-700' : 'text-neutral-300'}`} />
                <p className={`text-xl ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>No images yet</p>
                <p className={isDark ? 'text-neutral-500' : 'text-neutral-500'}>Upload your first image to get started</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {images.map((image) => (
                  <div
                    key={image.id}
                    className={`rounded-xl overflow-hidden ${isDark ? 'bg-neutral-900' : 'bg-white shadow-sm'}`}
                  >
                    <div className="aspect-square relative">
                      <img
                        src={getImageUrl(image.url)}
                        alt={image.title || 'Gallery image'}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className={`font-semibold truncate ${isDark ? 'text-white' : 'text-neutral-900'}`}>
                        {image.title || 'Untitled'}
                      </h3>
                      <p className={`text-sm capitalize ${isDark ? 'text-neutral-500' : 'text-neutral-500'}`}>
                        {getCategoryName(image.category)}
                      </p>
                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={() => setEditingImage(image)}
                          className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm transition-colors ${
                            isDark ? 'bg-neutral-800 hover:bg-neutral-700 text-white' : 'bg-neutral-100 hover:bg-neutral-200 text-neutral-900'
                          }`}
                        >
                          <Edit2 className="w-4 h-4" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteImage(image.id)}
                          className="flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm bg-red-500/10 text-red-500 hover:bg-red-500/20 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Categories Tab */}
        {activeTab === 'categories' && (
          <>
            {/* Stats & Actions */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
              <div>
                <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-neutral-900'}`}>Category Manager</h2>
                <p className={isDark ? 'text-neutral-400' : 'text-neutral-600'}>
                  {categories.length} categor{categories.length !== 1 ? 'ies' : 'y'} created
                </p>
              </div>
              <button
                onClick={() => setShowCategoryModal(true)}
                className="flex items-center gap-2 px-6 py-3 bg-lime-500 text-black font-semibold rounded-lg hover:bg-lime-400 transition-all"
              >
                <FolderPlus className="w-5 h-5" />
                Add Category
              </button>
            </div>

            {/* Categories List */}
            {categories.length === 0 ? (
              <div className={`text-center py-20 rounded-xl ${isDark ? 'bg-neutral-900' : 'bg-white'}`}>
                <Folder className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-neutral-700' : 'text-neutral-300'}`} />
                <p className={`text-xl ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>No categories yet</p>
                <p className={isDark ? 'text-neutral-500' : 'text-neutral-500'}>Create your first category to organize images</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {categories.map((category) => {
                  const imageCount = images.filter(img => img.category === category.slug).length;
                  return (
                    <div
                      key={category.id}
                      className={`p-6 rounded-xl ${isDark ? 'bg-neutral-900' : 'bg-white shadow-sm'}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-lime-500/20 rounded-lg flex items-center justify-center">
                            <Folder className="w-6 h-6 text-lime-500" />
                          </div>
                          <div>
                            <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-neutral-900'}`}>
                              {category.name}
                            </h3>
                            <p className={`text-sm ${isDark ? 'text-neutral-500' : 'text-neutral-500'}`}>
                              {imageCount} image{imageCount !== 1 ? 's' : ''}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeleteCategory(category.id, category.name)}
                          className="p-2 rounded-lg text-red-500 hover:bg-red-500/10 transition-colors"
                          title={imageCount > 0 ? 'Cannot delete - category has images' : 'Delete category'}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                      <div className={`mt-4 pt-4 border-t ${isDark ? 'border-neutral-800' : 'border-neutral-200'}`}>
                        <p className={`text-sm ${isDark ? 'text-neutral-500' : 'text-neutral-500'}`}>
                          Slug: <code className={`px-2 py-1 rounded ${isDark ? 'bg-neutral-800' : 'bg-neutral-100'}`}>{category.slug}</code>
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </main>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 overflow-y-auto">
          <div className={`w-full max-w-lg rounded-2xl my-8 ${isDark ? 'bg-neutral-900' : 'bg-white'}`}>
            <div className={`flex items-center justify-between p-6 border-b ${isDark ? 'border-neutral-800' : 'border-neutral-200'}`}>
              <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-neutral-900'}`}>Upload Image</h3>
              <button onClick={resetUploadForm} className="text-neutral-500 hover:text-neutral-300">
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleUpload} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              {/* File Input */}
              <div>
                <label className={`block mb-2 text-sm font-medium ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}>
                  Image File *
                </label>
                {uploadPreview ? (
                  <div className="relative">
                    <img src={uploadPreview} alt="Preview" className="w-full h-48 object-cover rounded-lg" />
                    <button
                      type="button"
                      onClick={() => { setUploadFile(null); setUploadPreview(null); }}
                      className="absolute top-2 right-2 p-1 bg-black/50 rounded-full text-white hover:bg-black/70"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className={`flex flex-col items-center justify-center w-full h-48 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                    isDark ? 'border-neutral-700 hover:border-lime-500 bg-neutral-800' : 'border-neutral-300 hover:border-lime-500 bg-neutral-50'
                  }`}>
                    <Upload className={`w-10 h-10 mb-2 ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`} />
                    <span className={isDark ? 'text-neutral-400' : 'text-neutral-600'}>Click to upload</span>
                    <span className={`text-sm ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`}>JPEG, PNG, WebP, GIF</span>
                    <input type="file" className="hidden" accept="image/*" onChange={handleFileSelect} />
                  </label>
                )}
              </div>

              {/* Title */}
              <div>
                <label className={`block mb-2 text-sm font-medium ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}>Title</label>
                <input
                  type="text"
                  value={uploadTitle}
                  onChange={(e) => setUploadTitle(e.target.value)}
                  className={`w-full px-4 py-3 rounded-lg border focus:border-lime-500 focus:outline-none ${
                    isDark ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-neutral-50 border-neutral-300 text-neutral-900'
                  }`}
                  placeholder="Image title"
                />
              </div>

              {/* Category */}
              <div>
                <label className={`block mb-2 text-sm font-medium ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}>Category</label>
                <select
                  value={uploadCategory}
                  onChange={(e) => setUploadCategory(e.target.value)}
                  className={`w-full px-4 py-3 rounded-lg border focus:border-lime-500 focus:outline-none ${
                    isDark ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-neutral-50 border-neutral-300 text-neutral-900'
                  }`}
                >
                  <option value="general">General</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.slug}>{cat.name}</option>
                  ))}
                </select>
              </div>

              {/* Description */}
              <div>
                <label className={`block mb-2 text-sm font-medium ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}>Description</label>
                <textarea
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                  rows={3}
                  className={`w-full px-4 py-3 rounded-lg border focus:border-lime-500 focus:outline-none resize-none ${
                    isDark ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-neutral-50 border-neutral-300 text-neutral-900'
                  }`}
                  placeholder="Optional description"
                />
              </div>

              <button
                type="submit"
                disabled={!uploadFile || uploading}
                className="w-full py-3 bg-lime-500 text-black font-semibold rounded-lg hover:bg-lime-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? 'Uploading...' : 'Upload Image'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingImage && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 overflow-y-auto">
          <div className={`w-full max-w-lg rounded-2xl my-8 ${isDark ? 'bg-neutral-900' : 'bg-white'}`}>
            <div className={`flex items-center justify-between p-6 border-b ${isDark ? 'border-neutral-800' : 'border-neutral-200'}`}>
              <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-neutral-900'}`}>Edit Image</h3>
              <button onClick={() => setEditingImage(null)} className="text-neutral-500 hover:text-neutral-300">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <img src={getImageUrl(editingImage.url)} alt="Preview" className="w-full h-48 object-cover rounded-lg" />
              
              <div>
                <label className={`block mb-2 text-sm font-medium ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}>Title</label>
                <input
                  type="text"
                  value={editingImage.title || ''}
                  onChange={(e) => setEditingImage({...editingImage, title: e.target.value})}
                  className={`w-full px-4 py-3 rounded-lg border focus:border-lime-500 focus:outline-none ${
                    isDark ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-neutral-50 border-neutral-300 text-neutral-900'
                  }`}
                />
              </div>

              <div>
                <label className={`block mb-2 text-sm font-medium ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}>Category</label>
                <select
                  value={editingImage.category || 'general'}
                  onChange={(e) => setEditingImage({...editingImage, category: e.target.value})}
                  className={`w-full px-4 py-3 rounded-lg border focus:border-lime-500 focus:outline-none ${
                    isDark ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-neutral-50 border-neutral-300 text-neutral-900'
                  }`}
                >
                  <option value="general">General</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.slug}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className={`block mb-2 text-sm font-medium ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}>Description</label>
                <textarea
                  value={editingImage.description || ''}
                  onChange={(e) => setEditingImage({...editingImage, description: e.target.value})}
                  rows={3}
                  className={`w-full px-4 py-3 rounded-lg border focus:border-lime-500 focus:outline-none resize-none ${
                    isDark ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-neutral-50 border-neutral-300 text-neutral-900'
                  }`}
                />
              </div>

              <button
                onClick={handleUpdateImage}
                className="w-full flex items-center justify-center gap-2 py-3 bg-lime-500 text-black font-semibold rounded-lg hover:bg-lime-400 transition-all"
              >
                <Save className="w-5 h-5" />
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className={`w-full max-w-md rounded-2xl ${isDark ? 'bg-neutral-900' : 'bg-white'}`}>
            <div className={`flex items-center justify-between p-6 border-b ${isDark ? 'border-neutral-800' : 'border-neutral-200'}`}>
              <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-neutral-900'}`}>Add Category</h3>
              <button onClick={() => { setShowCategoryModal(false); setNewCategoryName(''); }} className="text-neutral-500 hover:text-neutral-300">
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleCreateCategory} className="p-6 space-y-4">
              <div>
                <label className={`block mb-2 text-sm font-medium ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}>
                  Category Name *
                </label>
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  className={`w-full px-4 py-3 rounded-lg border focus:border-lime-500 focus:outline-none ${
                    isDark ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-neutral-50 border-neutral-300 text-neutral-900'
                  }`}
                  placeholder="e.g., Classic Cars"
                  autoFocus
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => { setShowCategoryModal(false); setNewCategoryName(''); }}
                  className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                    isDark ? 'bg-neutral-800 text-white hover:bg-neutral-700' : 'bg-neutral-100 text-neutral-900 hover:bg-neutral-200'
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!newCategoryName.trim() || creatingCategory}
                  className="flex-1 py-3 bg-lime-500 text-black font-semibold rounded-lg hover:bg-lime-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creatingCategory ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboardPage;
